import React from 'react';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';

import AdDisclosure from 'src/components/ad-disclosure';
import PrimaryGoalCTA from './cta';

import { useImpressionEvent } from 'src/utils/analytics/hooks';
import { useBrandedLPAnalytics } from '../context';

import {
  EntityName,
  EventType,
  SectionName,
} from 'src/utils/analytics/constants';

// Taken from the CardFinder's Branded LP Figma
// TODO: Remove once our theme palette is aligned with Figma
const BACKGROUND_COLOR = '#EFFCF8';

const headerText = Object.freeze({
  title: `NerdWallet's Cardfinder`,
  subTitle: function () {
    return (
      <>
        The smarter way to
        <br />
        find a credit card
      </>
    );
  },
  descriptionOne: `Get results in minutes! First, tell us why you want a new card.`,
  descriptionTwo: `This service is FREE, will not affect your credit score, and no
  contact information is required.`,
});

export const HeaderSection = () => {
  const { navigationId, impressionId } = useBrandedLPAnalytics();

  const { ref } = useImpressionEvent({
    eventType: EventType.ElementImpression,
    navigationId: navigationId,
    payload: {
      entity_name: EntityName.Flow,
      section_name: SectionName.JumpHeroCta,
      section_position: 0,
      parent_impression_id: impressionId,
    },
  });

  return (
    <Box
      ref={ref}
      bgcolor={BACKGROUND_COLOR}
      paddingLeft={{ xs: 2, xl: 3 }}
      paddingRight={{ xs: 2, xl: 0 }}
    >
      <Box marginLeft={{ xs: 2, md: 0 }} flexDirection="row" paddingTop={3}>
        <AdDisclosure />
      </Box>

      <Box
        display="flex"
        flexDirection={{ xs: 'column', xl: 'row' }}
        alignItems={{ xs: 'center', xl: 'flex-end' }}
        justifyContent={{ xs: 'center', xl: 'flex-end' }}
        gap={{ xs: 1, xl: 12 }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={5}
          paddingTop={6}
          paddingBottom={8}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems={{ xs: 'center', xl: 'flex-start' }}
            gap={3}
          >
            <Typography
              variant="textBold"
              textTransform="uppercase"
              color="primary.dark"
              fontSize={14}
            >
              {headerText.title}
            </Typography>
            <Typography
              variant="xlHeadline"
              color="primary.dark"
              textAlign={{ xs: 'center', xl: 'left' }}
            >
              {headerText.subTitle()}
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            alignItems={{ xs: 'center', xl: 'flex-start' }}
          >
            <Typography
              variant="bodyLargeBold"
              textAlign={{ xs: 'center', xl: 'left' }}
              maxWidth={{ xs: '90%', xl: '100%' }}
              sx={{ fontSize: '18px !important' }}
            >
              {headerText.descriptionOne}
            </Typography>

            <PrimaryGoalCTA />

            <Typography
              variant="textSmall"
              textAlign="center"
              maxWidth={{ xs: '90%', xl: '100%' }}
            >
              {headerText.descriptionTwo}
            </Typography>
          </Box>
        </Box>
        {/* The negative margin is set in order for
         the image to overflow into the next section */}
        <Box
          display="flex"
          flexDirection="column"
          marginBottom="-50px"
          marginRight={{ xs: '-0.5rem', xl: 0 }}
          alignSelf="flex-end"
          zIndex={1}
        >
          <Box minWidth={{ xs: 450, xl: 500 }}>
            <Image
              src="https://www.nerdwallet.com/cdn/cardfinder/credit-card.webp"
              alt=""
              style={{ width: '100%', height: 'auto' }}
              width={0}
              height={0}
              sizes="100vw"
              unoptimized
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
